import {Component, Input} from '@angular/core';
import {DefaultValues, IMAGE_SCALE_MAX, IMAGE_SCALE_MIN, MODULE_NAMES, PACKAGE_NAMES} from '@maporium-workspace/shared';
import {CommonModule} from '@angular/common';
import {ReactiveFormsModule} from '@angular/forms';
import {SliderInputComponent} from '../../../shared/forms/slider-input/slider-input.component';
import {TextInputComponent} from '../../../shared/forms/text-input/text-input.component';
import {SelectInputComponent} from '../../../shared/forms/select-input/select-input.component';
import {MatListModule} from '@angular/material/list';
import {ColorInputComponent} from '../../../shared/forms/color-input/color-input.component';
import {GraphNode, NodeShapeMapVisual, NodeTypesVisual} from '../../../api/interfaces/graph-node.interface';
import {TextHalignVisual, TextValignVisual} from '../../../api/interfaces/text-align.map';
import {HasModuleDirective} from '../../../packages/has-module.directive';
import {ImageReachabilityDirective} from '../../../shared/directives/url-validator/image-url-validator.directive';
import {AudioReachabilityDirective} from '../../../shared/directives/url-validator/audio-url-validator.directive';
import {TranslatePipe} from '@ngx-translate/core';
import {HasPackageDirective} from '../../../packages/has-package.directive';
import {MatTooltipModule} from '@angular/material/tooltip';
import {OpenaiService} from '../../../api/services/openai.service';
import {PackageService} from '../../../api/services/package.service';
import {AiStateService} from '../../../services/ai-state.service';
import {StyleGroupExpansionService} from '../../../shared/style-groups/style-group-expansion.service';
import {coreStyleGroupId, styleGroupToggleTooltip} from '../../../shared/style-groups/style-group.util';
import {StyleRowComponent} from '../../../shared/style-groups/style-row.component';
import {
  AIGenerationBubbleConfig,
  AiGenerationBubbleComponent
} from '../../../shared/components/ai-generation-bubble/ai-generation-bubble.component';
import {
  ImageWarningPreferenceService
} from '../../../shared/components/ai-generation-bubble/image-warning-preference.service';
import {ConnectedOverlayPositionChange, OverlayModule} from '@angular/cdk/overlay';
import {TrialStateService} from '../../../services/trial-state.service';
import {
  AI_BUBBLE_POSITIONS_END,
  isBubbleAbove
} from '../../../shared/components/ai-generation-bubble/ai-bubble-overlay';

/**
 * MAP-990: node STYLES section, reorganized into core styles (color, shape,
 * size, label font size, image, sound effect) each individually
 * expandable/collapsible to reveal their dependent styles, replacing the old
 * "light" (always visible) / "advanced" (behind a more/less button) split —
 * see NodeStyleAdvancedComponent's former content, now merged in here in the
 * ticket's required order. Core styles with no dependents (size, sound
 * effect) render no toggle at all (AC4).
 */
@Component({
  styles: [`
    /* MAP-1533: left-align the leading style icons so they start at the same
       horizontal position as the STYLES section header (matching the PROPERTIES
       and COMMENTS rows above), instead of sitting centered in their column with
       an equal gap on each side. Scoped to this style section only; the shared
       inputs keep their centered icon everywhere else they are used. */
    :host ::ng-deep .col-2.justify-content-center.align-items-center.d-flex {
      justify-content: flex-start !important;
      /* MAP-1533 round 2: a bootstrap col-2 (16.7% of the row) is far wider
         than the icon glyph needs; even left-aligned, the leftover width
         inside this column was a large dead gap before the field starts,
         narrowing the field itself. Give the icon a fixed footprint and let
         the field that follows reclaim the rest. */
      flex: 0 0 28px !important;
      max-width: 28px !important;
      width: 28px !important;
    }
    :host ::ng-deep .col-2.justify-content-center.align-items-center.d-flex + [class*="col-"] {
      flex: 1 1 0% !important;
      max-width: none !important;
      width: auto !important;
    }
    /* MAP-1533 round 2: the "generate image" button used to sit in a div
       wrapped AROUND app-text-input (row + a sibling flex container), which
       gave the image row a different DOM structure than every other style
       row and threw off its left alignment. It's now projected as ng-content
       INTO app-text-input's own field column, so the row stays a plain,
       unwrapped app-text-input identical to its siblings. */
    .find-image-slot {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      line-height: 1;
      margin-left: 6px;
      cursor: pointer;
    }
    .find-image-slot.is-finding {
      pointer-events: none;
      animation: findImagePulse 1s ease-in-out infinite;
    }
    @keyframes findImagePulse {
      0%, 100% { opacity: 0.35; }
      50% { opacity: 0.85; }
    }
    /* MAP-1583: the experimental-image warning bubble hangs off the generate
       button, which sits at the right edge of the sidebar (hence .arrow-end).
       It renders through a CDK connected overlay, not as an absolute child, so
       the scrolling panel body and the drawer's overflow:hidden cannot clip it
       (review round 2). */
    .find-image-anchor {
      display: inline-flex;
      align-items: center;
    }
  `],
  template: `
    <ng-container [formGroup]="formGroup">
      <!-- COLOR (core) -> color intensity, color opacity (dependents) -->
      <maporium-style-row *appHasModule="MODULE_NAMES.NODE_COLOR"
                          [divider]="false"
                          [hasToggle]="true"
                          [expanded]="isExpanded('color')"
                          [tooltip]="toggleTooltip('color')"
                          (toggleClick)="toggle('color')">
        <app-color-input [value]="focusedNode?.color || DefaultValues.node.color"
                         [placeholder]="'NODES.NODE_FILLING' | translate"
                         [icon]="'palette'"
                         formControlName="color"></app-color-input>
      </maporium-style-row>
      @if (isExpanded('color')) {
        <fieldset formGroupName="properties" *ngIf="focusedNode">
          <maporium-style-row [divider]="false">
            <app-slider-input [label]="'colors'"
                              [value]="toNumber(focusedNodeProperties.opacity !== undefined && focusedNodeProperties.opacity !== null ? focusedNodeProperties.opacity : DefaultValues.node.properties.opacity)"
                              [isIcon]="true"
                              [placeholder]="'NODES.SHAPE_FILLING' | translate"
                              formControlName="opacity"
                              [discrete]="true"
                              [max]="1"
                              [min]="0"
                              [step]="0.1">
            </app-slider-input>
          </maporium-style-row>
          <maporium-style-row [divider]="false"
                              *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer]">
            <app-slider-input [label]="'gradient'"
                              [value]="toNumber(focusedNodeProperties.colorOpacity !== undefined && focusedNodeProperties.colorOpacity !== null ? focusedNodeProperties.colorOpacity : DefaultValues.node.properties.colorOpacity)"
                              [isIcon]="true"
                              [placeholder]="'Set the node’s color opacity'"
                              formControlName="colorOpacity"
                              [discrete]="true"
                              [max]="1"
                              [min]="0"
                              [step]="0.1">
            </app-slider-input>
          </maporium-style-row>
        </fieldset>
      }

      <!-- SHAPE (core) -> shape thickness, shape stroke (dependents) -->
      <maporium-style-row *appHasModule="MODULE_NAMES.NODE_SHAPE"
                          [hasToggle]="true"
                          [expanded]="isExpanded('shape')"
                          [tooltip]="toggleTooltip('shape')"
                          (toggleClick)="toggle('shape')">
        <app-select-input formControlName="type"
                          [placeholder]="'NODES.SET_SHAPE' | translate"
                          [label]="'shapes'"
                          [isIcon]="true"
                          [options]="nodeTypeVisual">
        </app-select-input>
      </maporium-style-row>
      @if (isExpanded('shape')) {
        <maporium-style-row [divider]="false" *appHasModule="MODULE_NAMES.NODE_STROKE">
          <app-slider-input [label]="'line_weight'"
                            [value]="toNumber(focusedNode?.weight !== undefined ? focusedNode!.weight : DefaultValues.node.weight)"
                            [isIcon]="true"
                            [displayMultiplier]="2"
                            [placeholder]="'NODES.NODE_THICKNESS' | translate"
                            formControlName="weight"
                            [discrete]="true"
                            [max]="32"
                            [min]="0"
                            [step]="1"></app-slider-input>
        </maporium-style-row>
        <fieldset formGroupName="properties" *appHasModule="MODULE_NAMES.NODE_STROKE">
          <maporium-style-row [divider]="false">
            <app-select-input formControlName="borderStyle"
                              [label]="'line_style'"
                              [placeholder]="'NODES.SET_STROKE' | translate"
                              [isIcon]="true"
                              [options]="nodeBorderStyleVisual">
            </app-select-input>
          </maporium-style-row>
        </fieldset>
      }

      <!-- SIZE (core, no dependents -> no toggle) -->
      <maporium-style-row *appHasModule="MODULE_NAMES.NODE_SCALING">
        <fieldset formGroupName="properties">
          <app-slider-input [label]="'bubble_chart'"
                            [value]="focusedNode!.properties?.scale|| DefaultValues.node.properties.scale"
                            [isIcon]="true"
                            [discrete]="true"
                            [placeholder]="'NODES.NODE_SCALE' | translate"
                            formControlName="scale"
                            [max]="32"
                            [min]="0"
                            [displayMultiplier]="2"
                            [step]="1"></app-slider-input>
        </fieldset>
      </maporium-style-row>

      <!-- LABEL FONT SIZE (core) -> label property, label h/v position, label h/v offset (dependents) -->
      <maporium-style-row *ngIf="focusedNode"
                          [hasToggle]="true"
                          [expanded]="isExpanded('labelFontSize')"
                          [tooltip]="toggleTooltip('label font size')"
                          (toggleClick)="toggle('labelFontSize')">
        <fieldset formGroupName="properties">
          <app-slider-input [label]="'format_size'"
                            [value]="toNumber(focusedNode!.properties?.textFontSize !== undefined ? focusedNode!.properties?.textFontSize : DefaultValues.node.properties.textFontSize)"
                            [isIcon]="true"
                            [placeholder]="'NODES.SET_FONT_SIZE' | translate"
                            formControlName="textFontSize"
                            [discrete]="true"
                            [max]="32"
                            [min]="0"
                            [step]="1">
          </app-slider-input>
        </fieldset>
      </maporium-style-row>
      @if (isExpanded('labelFontSize')) {
        <fieldset formGroupName="properties" *ngIf="focusedNode">
          <maporium-style-row [divider]="false" *ngIf="packageService.isOnTestPlan()">
            <app-select-input formControlName="label"
                              class="mb-1"
                              [label]="'label'"
                              [noIcons]="true"
                              [truncate]="true"
                              [isIcon]="true"
                              [metadata]="focusedNode.metadata"
                              [placeholder]="'NODES.SET_LABEL' | translate">
            </app-select-input>
          </maporium-style-row>
          <ng-container *appHasModule="MODULE_NAMES.NODE_LABEL_ADJUSTMENT">
            <maporium-style-row [divider]="false">
              <app-select-input formControlName="textHalign"
                                [label]="'border_vertical'"
                                [placeholder]="'NODES.SET_HORIZONTAL_LABEL_POSITION' | translate"
                                [isIcon]="true"
                                [options]="textHalignVisual">
              </app-select-input>
            </maporium-style-row>
            <maporium-style-row [divider]="false">
              <app-select-input formControlName="textValign"
                                [label]="'border_horizontal'"
                                [placeholder]="'NODES.SET_VERTICAL_LABEL_POSITION' | translate"
                                [isIcon]="true"
                                [options]="textValignVisual">
              </app-select-input>
            </maporium-style-row>
            <maporium-style-row [divider]="false">
              <app-slider-input [label]="'text_rotation_none'"
                                [value]="toNumber(focusedNodeProperties.textMarginX !== undefined ? focusedNodeProperties.textMarginX : DefaultValues.node.properties.textMarginX)"
                                [isIcon]="true"
                                [placeholder]="'NODES.SET_HORIZONTAL_OFFSET' | translate"
                                formControlName="textMarginX"
                                [discrete]="true"
                                [showTickMarks]="true"
                                [max]="16"
                                [min]="-16"
                                [step]="1">
              </app-slider-input>
            </maporium-style-row>
            <maporium-style-row [divider]="false">
              <app-slider-input [label]="'text_rotate_vertical'"
                                [value]="toNumber(focusedNodeProperties.textMarginY !== undefined ? focusedNodeProperties.textMarginY : DefaultValues.node.properties.textMarginY)"
                                [isIcon]="true"
                                [placeholder]="'NODES.SET_VERTICAL_OFFSET' | translate"
                                formControlName="textMarginY"
                                [discrete]="true"
                                [showTickMarks]="true"
                                [max]="16"
                                [min]="-16"
                                [step]="1">
              </app-slider-input>
            </maporium-style-row>
          </ng-container>
        </fieldset>
      }

      <!-- IMAGE (core) -> image opacity (dependent) -->
      <maporium-style-row *appHasModule="MODULE_NAMES.NODE_IMAGE"
                          [hasToggle]="true"
                          [expanded]="isExpanded('image')"
                          [tooltip]="toggleTooltip('image')"
                          (toggleClick)="toggle('image')">
        <app-text-input formControlName="imageUrl"
                        [placeholder]="'Image URL'"
                        [tooltip]="'Set the node\\'s image via an image URL'"
                        appImageReachability
                        [autoFocusAndSelect]="true"
                        [label]="'image'"
                        [isIcon]="true">
          <!-- MAP-132: the generator still returns "almost valid" URLs too often to
               offer broadly; the field reclaims the width on every other plan. -->
          @if (packageService.isOnTestPlan() && aiStateService.isAiVisible()) {
            <span class="find-image-anchor">
              <span class="find-image-slot"
                    cdkOverlayOrigin
                    #findImageTrigger="cdkOverlayOrigin"
                    data-generate-button
                    [class.is-finding]="findingImage"
                    (click)="onFindImageClick()"
                    [matTooltipPosition]="'left'"
                    [matTooltip]="'Try to find an image URL on the Web to set the node\\'s image'">
                <span class="icon-smap-action-generate"></span>
              </span>
              <ng-template cdkConnectedOverlay
                           [cdkConnectedOverlayOrigin]="findImageTrigger"
                           [cdkConnectedOverlayOpen]="showImageWarning"
                           [cdkConnectedOverlayPositions]="imageWarningPositions"
                           [cdkConnectedOverlayViewportMargin]="8"
                           (positionChange)="onImageWarningPositionChange($event)"
                           (overlayKeydown)="onImageWarningKeydown($event)"
                           (detach)="showImageWarning = false">
                <app-ai-generation-bubble
                  class="arrow-end"
                  [class.arrow-down]="imageWarningAbove"
                  [config]="imageWarningConfig"
                  (generate)="onImageWarningGenerate()"
                  (close)="showImageWarning = false">
                </app-ai-generation-bubble>
              </ng-template>
            </span>
          }
        </app-text-input>
      </maporium-style-row>
      @if (isExpanded('image')) {
        <fieldset formGroupName="properties" *appHasModule="MODULE_NAMES.NODE_IMAGE">
          <maporium-style-row [divider]="false">
            <app-slider-input [label]="'picture_in_picture_center'"
                              [value]="focusedNodeProperties.imageScale ?? DefaultValues.node.properties.imageScale"
                              [isIcon]="true"
                              [discrete]="true"
                              [showTickMarks]="false"
                              [placeholder]="'NODES.SET_IMAGE_SCALE' | translate"
                              formControlName="imageScale"
                              [displayMultiplier]="100"
                              valueSuffix="%"
                              [step]="5"
                              [max]="IMAGE_SCALE_MAX * 100"
                              [min]="IMAGE_SCALE_MIN * 100">
            </app-slider-input>
          </maporium-style-row>
          <maporium-style-row [divider]="false">
            <app-slider-input [label]="'opacity'"
                              [value]="focusedNodeProperties.imageOpacity !== undefined ? focusedNodeProperties.imageOpacity : DefaultValues.node.properties.imageOpacity"
                              [isIcon]="true"
                              [discrete]="true"
                              [placeholder]="'NODES.SET_IMAGE_OPACITY' | translate"
                              formControlName="imageOpacity"
                              [step]="0.1"
                              [max]="1"
                              [min]="0">
            </app-slider-input>
          </maporium-style-row>
        </fieldset>
      }

      <!-- SOUND EFFECT (core, no dependents -> no toggle) -->
      <fieldset formGroupName="properties"
                *appHasPackage="[PACKAGE_NAMES.creator, PACKAGE_NAMES.analyst, PACKAGE_NAMES.publisher, PACKAGE_NAMES.developer]">
        <maporium-style-row>
          <app-text-input formControlName="soundUrl"
                          [placeholder]="'Sound URL'"
                          [tooltip]="'Set the node\\'s sound effect via a sound URL'"
                          appAudioReachability
                          [autoFocusAndSelect]="true"
                          [label]="'graphic_eq'"
                          [isIcon]="true"></app-text-input>
        </maporium-style-row>
      </fieldset>
    </ng-container>
  `,
  selector: 'maporium-node-style',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SliderInputComponent,
    TextInputComponent,
    SelectInputComponent,
    MatListModule,
    ColorInputComponent,
    HasModuleDirective,
    ImageReachabilityDirective,
    AudioReachabilityDirective,
    TranslatePipe,
    HasPackageDirective,
    MatTooltipModule,
    StyleRowComponent,
    AiGenerationBubbleComponent,
    OverlayModule,
  ],
  standalone: true,
})
export class NodeStyleComponent {
  protected readonly MODULE_NAMES = MODULE_NAMES;
  protected readonly DefaultValues = DefaultValues;
  protected readonly IMAGE_SCALE_MIN = IMAGE_SCALE_MIN;
  protected readonly IMAGE_SCALE_MAX = IMAGE_SCALE_MAX;
  public readonly nodeTypeVisual = NodeTypesVisual;
  public readonly nodeBorderStyleVisual = NodeShapeMapVisual;
  public readonly textHalignVisual = TextHalignVisual;
  public readonly textValignVisual = TextValignVisual;

  // MAP-1533 Part C: in-flight guard for the "find image" button. Disables the
  // button and drives the pulsing state while the web-search request runs.
  public findingImage = false;

  // MAP-1583: experimental-image warning bubble, shown as a confirmation gate
  // before the lookup runs (unless the user opted out of it).
  public showImageWarning = false;
  public imageWarningAbove = false;
  public readonly imageWarningConfig: AIGenerationBubbleConfig = {
    type: 'image-warning',
    trialLocked: true,
    imageWarningTarget: 'node',
  };
  public readonly imageWarningPositions = AI_BUBBLE_POSITIONS_END;

  @Input() formGroup: any;
  @Input() focusedNode: GraphNode | undefined;

  constructor(
    private openaiService: OpenaiService,
    protected packageService: PackageService,
    protected aiStateService: AiStateService,
    private styleGroups: StyleGroupExpansionService,
    private imageWarningPreference: ImageWarningPreferenceService,
    private trialStateService: TrialStateService,
  ) {
  }

  get focusedNodeProperties() {
    return this.focusedNode?.properties || {
      opacity: DefaultValues.node.properties.opacity,
      colorOpacity: DefaultValues.node.properties.colorOpacity,
      imageOpacity: DefaultValues.node.properties.imageOpacity,
      imageScale: DefaultValues.node.properties.imageScale,
      textMarginX: DefaultValues.node.properties.textMarginX.toString(),
      textMarginY: DefaultValues.node.properties.textMarginY.toString(),
    };
  }

  toNumber(value: any) {
    return value !== undefined && value !== null ? Number(value) : 0;
  }

  isExpanded(coreStyleId: string): boolean {
    return this.styleGroups.isExpanded(`node.${coreStyleId}`);
  }

  toggle(coreStyleId: string): void {
    this.styleGroups.toggle(`node.${coreStyleId}`);
  }

  toggleTooltip(coreStyleName: string): string {
    return styleGroupToggleTooltip(coreStyleName, this.isExpanded(coreStyleGroupId(coreStyleName)));
  }

  onFindImageClick() {
    if (this.findingImage) return;
    // MAP-1583 (review round 3): re-clicking the trigger closes the bubble.
    // The bubble's own outside-click guard skips [data-generate-button], so
    // without this the second click was a no-op.
    if (this.showImageWarning) {
      this.showImageWarning = false;
      return;
    }
    if (!this.imageSearchInput) return;

    this.trialStateService.refresh();
    // The trial bubble has no "do not show this again" box, so a previously
    // saved dismissal must not skip past it and start a lookup the trial
    // cannot run.
    if (!this.trialStateService.isOnTrial && this.imageWarningPreference.isDismissed()) {
      this.runFindImage();
      return;
    }
    this.showImageWarning = true;
  }

  // The bubble persists the "do not show this again" choice itself, the moment
  // it is ticked, so every close path honours it (MAP-1583 review round 3).
  onImageWarningGenerate() {
    if (this.trialStateService.isOnTrial) return;
    this.runFindImage();
  }

  onImageWarningKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.showImageWarning = false;
    }
  }

  onImageWarningPositionChange(change: ConnectedOverlayPositionChange) {
    this.imageWarningAbove = isBubbleAbove(change);
  }

  private get imageSearchInput(): { name: string; description: string } | null {
    const name = this.formGroup?.get('name')?.value || this.focusedNode?.name || '';
    const description = this.formGroup?.get('description')?.value || this.focusedNode?.description || '';
    return name || description ? {name, description} : null;
  }

  private runFindImage() {
    const input = this.imageSearchInput;
    if (!input) return;

    this.findingImage = true;
    this.openaiService.findImage(input.name, input.description, 'node').subscribe({
      next: (res) => {
        this.findingImage = false;
        this.showImageWarning = false;
        if (res?.imageUrl) {
          this.formGroup?.get('imageUrl')?.setValue(res.imageUrl);
        }
      },
      error: () => {
        this.findingImage = false;
        this.showImageWarning = false;
      }
    });
  }

  protected readonly PACKAGE_NAMES = PACKAGE_NAMES;
}
